import axios from 'axios';

const BASE_URL = 'https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Courses API
export const getCourses = async () => {
  try {
    const response = await api.get('/mentorship/courses');
    return response.data;
  } catch (error) {
    console.error('Error getting courses:', error);
    throw error;
  }
};

export const createCourse = async (courseData) => {
  try {
    // If numberOfBatches is provided, we'll create multiple batches
    if (courseData.numberOfBatches > 0) {
      // Parse course IDs if provided
      let courseIds = [];
      if (courseData.courseIds) {
        courseIds = courseData.courseIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
      }

      // Determine batch naming format
      const isAlphabetical = courseData.batchNamingFormat !== 'numerical';
      
      // Make sure batchPrefix is available, default to courseName if not
      const batchPrefix = courseData.batchPrefix || courseData.courseName;
      
      // First batch suffix based on format
      const firstBatchSuffix = isAlphabetical ? 'A' : '1';
      const firstBatchId = `${batchPrefix}-${firstBatchSuffix}`;
      
      // Update the batchName for the first batch
      const updatedCourseData = {
        ...courseData,
        batchName: firstBatchId,
        courseId: courseIds.length > 0 ? courseIds[0] : undefined
      };
      
      // Create the course with the first batch
      const response = await api.post('/mentorship/createCourse', updatedCourseData);
      
      // Generate additional batches
      const additionalBatches = [];
      
      // Generate batch suffixes based on format
      if (isAlphabetical) {
        // Generate alphabetical sequence (B, C, D, ... Z, AA, AB, etc.)
        const generateAlphabeticalSuffix = (index) => {
          // Convert to base-26 representation using alphabets
          let suffix = '';
          let num = index;
          
          while (num > 0) {
            num--; // Adjust for 0-based index
            const remainder = num % 26;
            suffix = String.fromCharCode(65 + remainder) + suffix; // 65 is ASCII for 'A'
            num = Math.floor(num / 26);
          }
          
          return suffix || 'A'; // Default to 'A' if empty
        };
        
        // Start from B (index 1) since A was created already
        for (let i = 1; i < courseData.numberOfBatches; i++) {
          const batchSuffix = generateAlphabeticalSuffix(i + 1);
          additionalBatches.push({
            PK: courseData.courseName,
            SK: `${batchPrefix}-${batchSuffix}`,
            courseId: courseIds.length > i ? courseIds[i] : undefined,
            capacity: courseData.initialCourseCount,
            count: 0,
            courseUrl: courseData.courseUrl
          });
        }
      } else {
        // Generate numerical sequence (2, 3, 4, etc.)
        for (let i = 1; i < courseData.numberOfBatches; i++) {
          additionalBatches.push({
            PK: courseData.courseName,
            SK: `${batchPrefix}-${i + 1}`,
            courseId: courseIds.length > i ? courseIds[i] : undefined,
            capacity: courseData.initialCourseCount,
            count: 0,
            courseUrl: courseData.courseUrl
          });
        }
      }
      
      // Create additional batches
      for (const batchData of additionalBatches) {
        await createBatch(batchData);
      }
      
      return response.data;
    } else {
      // Regular course creation with just one batch
      const isAlphabetical = courseData.batchNamingFormat !== 'numerical';
      const batchPrefix = courseData.batchPrefix || courseData.courseName;
      const firstBatchSuffix = isAlphabetical ? 'A' : '1';
      const firstBatchId = `${batchPrefix}-${firstBatchSuffix}`;
      
      const updatedCourseData = {
        ...courseData,
        batchName: firstBatchId
      };
      
      const response = await api.post('/mentorship/createCourse', updatedCourseData);
      return response.data;
    }
  } catch (error) {
    console.error('Error creating course:', error);
    throw error;
  }
};

// Batch API
export const getBatchesByCourse = async (courseName) => {
  try {
    const response = await api.get(`/mentorship/course/${encodeURIComponent(courseName)}/batchCounts`);
    return response.data;
  } catch (error) {
    console.error(`Error getting batches for course ${courseName}:`, error);
    throw error;
  }
};

export const createBatch = async (batchData) => {
  try {
    // Use the createCourse endpoint with appropriate parameters for batch creation
    const response = await api.post('/mentorship/createCourse', {
      courseName: batchData.PK,
      batchName: batchData.SK,
      courseId: batchData.courseId,
      courseUrl: batchData.courseUrl,
      initialCourseCount: batchData.capacity,
      currentCourseCount: batchData.count || 0
    });
    return response.data;
  } catch (error) {
    console.error('Error creating batch:', error);
    throw error;
  }
};

// Update batch subscription ID
export const updateBatchSubscription = async (courseName, batchId, subscriptionId) => {
  try {
    const response = await api.put(
      `/mentorship/batch/${encodeURIComponent(courseName)}/${encodeURIComponent(batchId)}/subscription`,
      { subscriptionId }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating batch subscription ID:', error);
    throw error;
  }
};

// Students API
export const getStudentsByCourse = async (courseName, assignedFilter) => {
  try {
    let url = `/mentorship/course/${encodeURIComponent(courseName)}/students`;
    
    // Add assigned filter if specified
    if (assignedFilter === 'assigned') {
      url += '?assigned=true';
    } else if (assignedFilter === 'unassigned') {
      url += '?assigned=false';
    }
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error getting students for course ${courseName}:`, error);
    throw error;
  }
};

export const addStudent = async (studentData) => {
  try {
    const response = await api.post('/mentorship/addStudent', studentData);
    return response.data;
  } catch (error) {
    console.error('Error adding student:', error);
    throw error;
  }
};

export const reassignStudent = async (reassignmentData) => {
  try {
    const response = await api.put('/mentorship/reassignStudent', reassignmentData);
    return response.data;
  } catch (error) {
    console.error('Error reassigning student:', error);
    throw error;
  }
};

// CSV Upload
export const uploadCSV = async (formData) => {
  try {
    const response = await api.post('/mentorship/uploadCSV', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading CSV:', error);
    throw error;
  }
};

// Upload students CSV (append mode)
export const uploadStudentsCSV = async (formData) => {
  try {
    const response = await api.post('/mentorship/uploadStudentsCSV', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading students CSV:', error);
    throw error;
  }
};

// Export unassigned students to CSV
export const exportUnassignedStudents = async (courseName) => {
  try {
    const response = await api.get(`/mentorship/course/${encodeURIComponent(courseName)}/students/exportUnassigned`, {
      responseType: 'blob' // Important for handling CSV file response
    });
    return response.data;
  } catch (error) {
    console.error(`Error exporting unassigned students for course ${courseName}:`, error);
    throw error;
  }
};

// Mark students as assigned
export const markStudentsAssigned = async (courseName, studentSKs) => {
  try {
    const response = await api.post(`/mentorship/course/${encodeURIComponent(courseName)}/students/markAssigned`, {
      studentSKs
    });
    return response.data;
  } catch (error) {
    console.error('Error marking students as assigned:', error);
    throw error;
  }
};

// Mark batches as assigned
export const markBatchesAssigned = async (courseName, batchIds) => {
  try {
    const response = await api.post(`/mentorship/course/${encodeURIComponent(courseName)}/batches/markAssigned`, {
      batchIds
    });
    return response.data;
  } catch (error) {
    console.error('Error marking batches as assigned:', error);
    throw error;
  }
};

// Zoom Meeting Schedule API
export const scheduleZoomMeeting = async (meetingData) => {
  try {
    const response = await api.post('/mentorship/zoom-meeting-schedule', meetingData);
    return response.data;
  } catch (error) {
    console.error('Error scheduling zoom meeting:', error);
    throw error;
  }
};

// Get all mentorship meetings
export const getMentorshipMeetings = async (filters = {}) => {
  try {
    let url = '/mentorship/mentorship-meetings';
    const queryParams = [];
    
    if (filters.mentorName) {
      queryParams.push(`mentorName=${encodeURIComponent(filters.mentorName)}`);
    }
    
    if (filters.batchName) {
      queryParams.push(`batchName=${encodeURIComponent(filters.batchName)}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error getting mentorship meetings:', error);
    throw error;
  }
};

// Update batch details (courseId, courseUrl, capacity, and optionally rename batch)
export const updateBatchDetails = async (courseName, batchId, details) => {
  try {
    console.log(`Sending PUT request to /mentorship/batch/${encodeURIComponent(courseName)}/${encodeURIComponent(batchId)}`, details);
    
    // Ensure details has the expected fields, even if they are null
    const normalizedDetails = {
      courseId: details.courseId !== undefined ? details.courseId : null,
      courseUrl: details.courseUrl !== undefined ? details.courseUrl : null,
      capacity: details.capacity !== undefined ? parseInt(details.capacity) : 50
    };
    
    // Include newBatchId for renaming if provided
    if (details.newBatchId) {
      normalizedDetails.newBatchId = details.newBatchId;
      console.log(`Renaming batch from ${batchId} to ${details.newBatchId}`);
    }
    
    const response = await api.put(
      `/mentorship/batch/${encodeURIComponent(courseName)}/${encodeURIComponent(batchId)}`, 
      normalizedDetails,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log("Update batch response:", response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating batch details:', error);
    // Log more detailed information about the error
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request made but no response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
};

// Delete course and all its data
export const deleteCourse = async (courseName) => {
  try {
    const response = await api.delete(`/mentorship/course/${encodeURIComponent(courseName)}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting course ${courseName}:`, error);
    throw error;
  }
};

// Search students by name, phone, or email in a course
export const searchStudentsByCourse = async (courseName, searchTerm) => {
  try {
    const response = await api.get(`/mentorship/course/${encodeURIComponent(courseName)}/students/search`, {
      params: { q: searchTerm }
    });
    return response.data;
  } catch (error) {
    console.error(`Error searching students for course ${courseName}:`, error);
    throw error;
  }
};

// Delete a student from a course
export const deleteStudent = async (courseName, batchId, phone) => {
  try {
    const response = await api.post('/mentorship/deleteStudent', { courseName, batchId, phone });
    return response.data;
  } catch (error) {
    console.error('Error deleting student:', error);
    throw error;
  }
};

// Batch delete students via CSV
export const batchDeleteStudentsCSV = async (formData) => {
  try {
    const response = await api.post('/mentorship/batchDeleteStudentsCSV', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error batch deleting students:', error);
    throw error;
  }
};

export default {
  getCourses,
  createCourse,
  getBatchesByCourse,
  createBatch,
  getStudentsByCourse,
  addStudent,
  reassignStudent,
  uploadCSV,
  uploadStudentsCSV,
  exportUnassignedStudents,
  markStudentsAssigned,
  updateBatchDetails,
  deleteCourse,
  updateBatchSubscription,
  scheduleZoomMeeting,
  getMentorshipMeetings,
  markBatchesAssigned,
  searchStudentsByCourse,
  deleteStudent,
  batchDeleteStudentsCSV
}; 